import { useEffect } from 'react';

import { Box, Stack, Radio, SxProps, Theme } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

import { resolvePhoneNumberFormat } from '@/utils';
import { BffFacility, BffWorkingHours } from '@/graphql/__generated/sdk';

import OfficeStatus from './OfficeStatus';
import LocationSearchInput from './LocationSearchInput';
import LocationCardActions from './LocationCardActions';
import CustomTypography from './UI/Typography/CustomTypography';
import CustomButton from './UI/Button/CustomButton';
import { locationDrawerStyles } from './Header.styles';
import { ExtendedFacility } from './Header';

interface LocationDrawerProps {
	displayedOffices: BffFacility[];
	selectedOfficeCode: string;
	setSelectedOfficeCode: (code: string) => void;
	searchError: string | null;
	selectedOffice?: ExtendedFacility;
	onLocationSearch: (query: string) => void;
	onSetAsMyLocationClick: () => void;
	onScheduleClick: (scheduleUrl: string, linkText: string) => void;
}

export function LocationDrawer({
	displayedOffices,
	selectedOfficeCode,
	setSelectedOfficeCode,
	searchError,
	selectedOffice,
	onLocationSearch,
	onSetAsMyLocationClick,
	onScheduleClick,
}: LocationDrawerProps) {
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		(globalThis as any).Invoca?.PNAPI?.run();
	}, [selectedOfficeCode]);

	return (
		<>
			{/* Location search input */}
			<Box sx={locationDrawerStyles.locationSearchContainer}>
				<LocationSearchInput onSearch={onLocationSearch} />
			</Box>
			{searchError && (
				<Box sx={locationDrawerStyles.searchErrorBox}>
					<InfoOutlined sx={locationDrawerStyles.searchErrorIcon} style={{ fontSize: 20 }} />
					<CustomTypography variant="bodyS" sx={locationDrawerStyles.searchErrorText}>
						{searchError}
					</CustomTypography>
				</Box>
			)}

			{displayedOffices.length > 0 && (
				<Box id="nearby-offices-list-container">
					<Stack spacing={1} sx={locationDrawerStyles.officeListStack}>
						{displayedOffices.map((facility: BffFacility) => {
							const isSelected = selectedOfficeCode
								? selectedOfficeCode === facility.code
								: selectedOffice?.code === facility.code;
							return (
								<Box
									key={facility.code}
									sx={
										{
											...locationDrawerStyles.officeCard,
											...(isSelected ? locationDrawerStyles.officeCardSelected : {}),
										} as SxProps<Theme>
									}
								>
									<Box sx={locationDrawerStyles.officeCardInner}>
										<Box sx={locationDrawerStyles.officeNameRow}>
											<CustomTypography variant="bodyL" sx={locationDrawerStyles.officeName}>
												{facility.name}
											</CustomTypography>
											<Radio
												checked={isSelected}
												onChange={() => setSelectedOfficeCode(facility.code)}
												value={facility.code}
												sx={{
													...locationDrawerStyles.radioNoPadding,
													'&.Mui-checked': {
														color: '#17B26A',
													},
												}}
											/>
										</Box>

										<Box
											display="flex"
											flexDirection="column"
											gap="0.25rem"
											alignItems="flex-start"
											sx={locationDrawerStyles.officeDetailsOverflow}
										>
											<CustomTypography variant="bodyS">
												{facility?.address?.address1}, {facility?.address?.city},{' '}
												{facility?.address?.stateCode} {facility?.address?.zipCode}
												{facility?.location?.distance
													? ` • ${facility?.location?.distance.toFixed(1)} mi`
													: ''}
											</CustomTypography>
											{isSelected ? (
												<CustomTypography variant="bodyS" className="InvocaNumber">
													{resolvePhoneNumberFormat(facility?.phoneNumber || '')}
												</CustomTypography>
											) : null}
											<OfficeStatus
												workingHours={facility?.workingHours as BffWorkingHours[]}
												timeZoneId={facility?.location?.timeZone ?? ''}
											/>
										</Box>
									</Box>
									{isSelected ? (
										<LocationCardActions
											selectedOffice={facility as ExtendedFacility}
											onScheduleClick={onScheduleClick}
										/>
									) : null}
								</Box>
							);
						})}
					</Stack>
				</Box>
			)}

			{selectedOfficeCode && selectedOfficeCode !== selectedOffice?.code && (
				<Box sx={locationDrawerStyles.locationFooterActions}>
					<CustomButton fullWidth variantType="primary" onClick={onSetAsMyLocationClick}>
						Set as my location
					</CustomButton>
				</Box>
			)}
		</>
	);
}
